import { Box } from '@tanium/react-box';
import { FormRow } from '@tanium/react-form';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { Field, FieldProps, FormikErrors } from 'formik';
import get from 'lodash.get';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TARGET_ACCESS_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import APIAccessType from 'common/IAPIAccessType';
import { K8sRoleTargetAccess } from 'gql/K8sRoleTargetAccess';
import IDropdownOption from 'common/IDropdownOption';

interface KubernetesRoleAccessProps {
  k8sTargetAccessList?: K8sRoleTargetAccess[];
}

export const NAMESPACE = 'namespace';
export const ROLE = 'role';

export const KubernetesRoleAccess = ({ k8sTargetAccessList }: KubernetesRoleAccessProps): React.ReactElement => {
  const { t } = useTranslation();
  const [namespace, setNamespace] = useState('');
  const formInputIdPrefix = 'create-request-';

  const roleOptions = useCallback((): IDropdownOption[] => {
    if (k8sTargetAccessList) {
      return k8sTargetAccessList
        .filter((ta) => ta.namespace === namespace && ta.apiAccessType === APIAccessType.ROLE)
        .map((ta) => {
          return {
            label: ta.roleName,
            value: ta.id,
          };
        });
    }
    return [];
  }, [k8sTargetAccessList, namespace]);

  const namespaceOptions = useCallback((): IDropdownOption[] => {
    if (k8sTargetAccessList) {
      const uniqueNamespaces = [
        ...new Set(
          k8sTargetAccessList
            .filter((ta) => ta.apiAccessType === APIAccessType.ROLE)
            .map((ta) => ta.namespace)
            .sort()
        ),
      ];
      return uniqueNamespaces.map((ns) => {
        return {
          label: ns,
          value: ns,
        };
      });
    }
    return [];
  }, [k8sTargetAccessList]);

  return (
    <>
      <Box display="inline-flex" mr={1}>
        <Field name={NAMESPACE}>
          {({ field, form }: FieldProps): React.ReactElement => {
            return (
              <FormRow
                label={t('NAMESPACE')}
                required
                description={t('NAMESPACE_DESCRIPTION')}
                errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
                labelId={`${formInputIdPrefix}${field.name}`}
                data-testid={`${formInputIdPrefix}${field.name}`}
              >
                <FormSelectInput
                  onChange={(option): void => {
                    void form.setFieldValue(field.name, option.value);
                    setNamespace(option.value);
                  }}
                  value={namespaceOptions().find((option) => option.value === field.value) || ''}
                  inputId={`${formInputIdPrefix}${field.name}`}
                  options={namespaceOptions()}
                  aria-label={t('NAMESPACE')}
                />
              </FormRow>
            );
          }}
        </Field>
      </Box>
      <Box display="inline-flex" mr={1}>
        <Field name={TARGET_ACCESS_ID}>
          {({ field, form }: FieldProps): React.ReactElement => {
            return (
              <FormRow
                label={t('KUBERNETES_ROLE')}
                required
                description={t('KUBERNETES_ROLE_DESCRIPTION')}
                errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
                labelId={`${formInputIdPrefix}${field.name}`}
                data-testid={`${formInputIdPrefix}${field.name}`}
              >
                <FormSelectInput
                  onChange={(option): void => {
                    void form.setFieldValue(ROLE, option.label);
                    void form.setFieldValue(field.name, option.value);
                  }}
                  value={roleOptions().find((option) => option.value === field.value) || ''}
                  inputId={`${formInputIdPrefix}${field.name}`}
                  options={roleOptions()}
                  aria-label={t('KUBERNETES_ROLE')}
                />
              </FormRow>
            );
          }}
        </Field>
      </Box>
    </>
  );
};

export default KubernetesRoleAccess;
